const React = require('react')
const { useState, useEffect } = require('react')
const { useHistory, useLocation } = require('react-router-dom')
const { Container, Col, Row, Table, UncontrolledTooltip } = require('reactstrap')
const {
    useBlockchainData,
    useFormatBytes,
    useHexToString,
    useFormatTimestampShort,
    useFormatDateTime,
    useResponsive,
    BREAKPOINTS
} = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { LoadingSpinner } = require('@components')
const { TableHeader, TableFooter } = require('@components/table')

require('@styles/pages/blocks/index.css')

const Slots = () => {
    const location = useLocation()
    const history = useHistory()
    const { network } = useNetworkConfig()
    const { getSlots } = useBlockchainData(network.name)
    const formatBytes = useFormatBytes()
    const formatHexToString = useHexToString()
    const formatTimestamp = useFormatTimestampShort()
    const formatDateTime = useFormatDateTime()

    const params = new URLSearchParams(location.search)

    const [epochNumber, setEpochNumber] = useState(Number(params.get('epoch')) || null)
    const [page, setPage] = useState(Number(params.get('p')) || 1)
    const [limit, setLimit] = useState(Number(params.get('ps')) || 25)
    const [slotsData, setSlotsData] = useState()
    const [showAge, setShowAge] = useState(true)

    const isMobile = useResponsive(BREAKPOINTS.medium)

    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const epochNumberParam = Number(params.get('epoch'))
        const pageParam = Number(params.get('p'))
        const limitParam = Number(params.get('ps'))

        if (epochNumberParam && epochNumberParam !== epochNumber) {
            setEpochNumber(epochNumberParam)
        }

        if (pageParam && pageParam > 0 && pageParam !== page) {
            setPage(pageParam)
        }

        if (limitParam && limitParam > 0 && limitParam !== limit) {
            setLimit(limitParam)
        }
    }, [location.search])

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)

            try {
                const [slotsResponse] = await Promise.all([getSlots(page, limit, { epoch: epochNumber })])

                if (slotsResponse) {
                    setSlotsData(slotsResponse)
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [network, page, limit])

    return (
        <div className='page'>
            <Container>
                <Col>
                    <Row>
                        <div className='page-header'>
                            <text className='page-title'>Slots</text>
                            {epochNumber && (
                                <div style={{ display: 'block', marginTop: '8px' }}>
                                    <text className='page-title-subcontent'>
                                        For Epoch&nbsp;
                                        <span
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() => history.push(`/${network.name}/epoch/${epochNumber}`)}
                                        >
                                            {epochNumber}
                                        </span>
                                    </text>
                                </div>
                            )}
                            <div className='page-title-border' />
                        </div>
                    </Row>
                    <div className='box table-section' style={{ padding: '24px 24px 0' }}>
                        {loading ? (
                            <div
                                className='d-flex justify-content-center align-items-center'
                                style={{ height: '300px' }}
                            >
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                <TableHeader
                                    title={`Total of ${slotsData?.totalItems.toLocaleString()} slots`}
                                    page={page}
                                    limit={limit}
                                    totalPages={slotsData?.totalPages}
                                />
                                <Table
                                    responsive
                                    style={
                                        isMobile ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' } : {}
                                    }
                                >
                                    <thead>
                                        <tr>
                                            <th>Epoch</th>
                                            <th>Slot</th>
                                            <th>Pos</th>
                                            <th>Status</th>
                                            <th>
                                                <span
                                                    id='age-tooltip'
                                                    className={`${network.name}-text-color cursor-pointer`}
                                                    onClick={() => setShowAge(!showAge)}
                                                >
                                                    {showAge ? 'Age' : 'Date Time (UTC)'}
                                                </span>
                                                <UncontrolledTooltip placement='top' target='age-tooltip'>
                                                    {showAge
                                                        ? 'Click to show Datetime Format'
                                                        : 'Click to show Age Format'}
                                                </UncontrolledTooltip>
                                            </th>
                                            <th>Proposer</th>
                                            <th>Parent Root</th>
                                            <th>Att</th>
                                            <th>Dep</th>
                                            <th>S-P/A</th>
                                            <th>Exit</th>
                                            <th>Graffiti Utf8</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {slotsData?.items.map(slot => (
                                            <tr key={slot.slot}>
                                                <td>
                                                    <span
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(`/${network.name}/epoch/${slot.epoch}`)
                                                        }
                                                    >
                                                        {slot.epoch}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(`/${network.name}/slot/${slot.slot}`)
                                                        }
                                                    >
                                                        {slot.slot}
                                                    </span>
                                                </td>
                                                <td>
                                                    {(slot.slot % 32) + 1 === 32 ? (
                                                        <>{(slot.slot % 32) + 1} of 32</>
                                                    ) : (
                                                        <span style={{ color: 'var(--grey-color)' }}>
                                                            {(slot.slot % 32) + 1} of 32
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span style={{ color: 'var(--success-color)' }}>proposed</span>
                                                </td>
                                                <td>
                                                    {showAge ? (
                                                        <>
                                                            <span id={'age-' + slot.slot}>
                                                                {formatTimestamp(slot.timestamp)}
                                                            </span>
                                                            <UncontrolledTooltip
                                                                placement='top'
                                                                target={`age-${slot.slot}`}
                                                            >
                                                                {formatDateTime(slot.timestamp)}
                                                            </UncontrolledTooltip>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span id={'datetime-' + slot.slot}>
                                                                {formatDateTime(slot.timestamp)}
                                                            </span>
                                                            <UncontrolledTooltip
                                                                placement='top'
                                                                target={`datetime-${slot.slot}`}
                                                            >
                                                                {formatTimestamp(slot.timestamp)}
                                                            </UncontrolledTooltip>
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    <i
                                                        className='fas fa-user'
                                                        style={{
                                                            fontSize: '14px',
                                                            color: 'var(--grey-color)',
                                                            marginRight: '10px'
                                                        }}
                                                    />
                                                    <span
                                                        id={`proposer-${slot.slot}-tooltip`}
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(
                                                                `/${network.name}/validator/${slot.proposerIndex}`
                                                            )
                                                        }
                                                    >
                                                        {slot.proposerIndex}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`proposer-${slot.slot}-tooltip`}
                                                    >
                                                        Validator PubKey: {slot.proposerPubkey}
                                                    </UncontrolledTooltip>
                                                </td>
                                                <td>
                                                    <span
                                                        id={`parent-root-${slot.slot}-tooltip`}
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(`/${network.name}/slot/${slot.slot - 1}`)
                                                        }
                                                    >
                                                        {formatBytes(slot.parentRoot, 64, 10, 0)}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`parent-root-${slot.slot}-tooltip`}
                                                    >
                                                        {slot.parentRoot}
                                                    </UncontrolledTooltip>
                                                </td>
                                                <td>{slot.attestationsCount}</td>
                                                <td>{slot.depositsCount}</td>
                                                <td>
                                                    {slot.proposerSlashingsCount} / {slot.attesterSlashingsCount}
                                                </td>
                                                <td>{slot.voluntaryExitsCount}</td>
                                                <td>{formatHexToString(slot.graffiti)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <TableFooter
                                    limit={limit}
                                    setLimit={setLimit}
                                    page={page}
                                    setPage={setPage}
                                    totalPages={slotsData?.totalPages}
                                />
                            </>
                        )}
                    </div>
                </Col>
            </Container>
        </div>
    )
}

module.exports = Slots
